import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import { Candles } from "@/components/site/Candles";
import { Ladybug } from "@/components/site/Ladybug";

const SLIDES = [
  ["/img/slide1.jpg", "A healer labeling remedies in her apothecary"],
  ["/img/slide2.jpg", "Hands grinding herbs in a stone mortar"],
  ["/img/slide3.jpg", "Students pouring oils in a healing lab"],
  ["/img/slide4.jpg", "An elder healer pouring a tincture"],
];

const Slides = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      {SLIDES.map(([src, alt], n) => (
        <img key={src} src={src} alt={alt} data-testid={`hero-slide-${n}`}
          className={`slide absolute inset-0 h-full w-full object-cover ${n === i ? "opacity-100 scale-105" : "opacity-0 scale-100"}`} />
      ))}
      <div className="absolute bottom-8 right-8 z-20 flex gap-2">
        {SLIDES.map(([src], n) => (
          <button key={src} onClick={() => setI(n)} aria-label={`Slide ${n + 1}`} data-testid={`hero-dot-${n}`}
            className={`h-2 rounded-full transition-all duration-500 ${n === i ? "w-8 bg-[#faf7f2]" : "w-2 bg-[#faf7f2]/50"}`} />
        ))}
      </div>
    </>
  );
};

const PATHS = [
  ["/clinic", "The Healers Clinic", "where your healing story begins"],
  ["/clinic#womb", "The Healing Womb", "a sanctuary for mothers"],
  ["/university", "The Healers University", "where the called become the healers"],
  ["/ambassadors", "The Healing Ambassadors", "the keepers and carriers of this work"],
  ["/members/ceremonies", "Sacred Ceremony", "where community gathers and remembers"],
];

const Hero = () => (
  <section className="relative overflow-hidden min-h-[92vh] flex items-center" data-testid="home-hero">
    <Slides />
    <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#1c2314]/90 via-[#1c2314]/60 to-[#1c2314]/10" />
    <div className="relative z-10 max-w-7xl w-full mx-auto px-6 lg:px-10 py-24">
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[#b1cc90] rise inline-flex items-center gap-3">A Medicine Community <Ladybug size={18} rotate={30} /></span>
        <h1 data-testid="home-hero-title" className="font-serif-h font-light text-5xl sm:text-6xl lg:text-7xl text-[#faf7f2] leading-[1.05] mt-6 rise d1">
          You were not built to break. <em className="text-[#e2b6d2]">You are built to heal and bloom.</em>
        </h1>
        <p className="mt-10 text-base sm:text-lg leading-relaxed text-[#faf7f2]/85 max-w-xl rise d2">
          We are a medicine community, a living circle rooted in ceremony, tradition, and the remembrance of those who came before us. Our ancestors carried this medicine through fire and famine, through silence and survival. It lives in us now.
        </p>
        <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#faf7f2]/85 max-w-xl rise d3">
          The Healers are the ones who get the work done, because this is how we survived, and this is how we thrive.
        </p>
        <div className="mt-10 flex flex-wrap gap-4 rise d4">
          <Link to="/apply" data-testid="home-apply-now-btn" className="btn btn-mauve">Apply Now <Ladybug size={16} rotate={-20} /></Link>
          <Link to="/ambassadors?mode=signup#join" data-testid="home-join-ambassadors-btn" className="btn btn-olive">Apply to the Healing Ambassadors</Link>
          <Link to="/members/rsvp/light" data-testid="home-rsvp-light-btn" className="btn border border-[#faf7f2]/70 text-[#faf7f2] hover:bg-[#faf7f2] hover:text-[#232b1b]">Ambassadors: RSVP for the Light Ceremony</Link>
        </div>
      </div>
    </div>
  </section>
);

const Law = () => (
  <section className="bg-[#dacac1]/45">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-28 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <span className="eyebrow">Our Way</span>
        <h2 className="font-serif-h text-4xl sm:text-5xl text-[#577731] mt-4">The Law of the Medicine <Ladybug size={24} rotate={-35} className="align-top" /></h2>
        <p className="font-serif-h italic text-2xl sm:text-3xl text-[#b6689a] mt-8 leading-snug">
          Every wound holds a teaching. Every ache holds a door.
        </p>
      </div>
      <div className="lg:col-span-7 lg:pt-20">
        <p className="text-base sm:text-lg leading-loose text-[#232b1b]/85">
          We do not run from pain. We sit with it, tend it, and transmute it. What still aches, what still bleeds, what has never been allowed to heal: we meet it with holistic care, sacred ritual, and ancestral wisdom. Sickness becomes strength. Imbalance becomes alignment. The deepest wounds become symbols of wisdom, power, and divine purpose.
        </p>
        <p className="font-serif-h text-3xl text-[#577731] mt-10">This is not a quick fix. This is a homecoming.</p>
      </div>
    </div>
  </section>
);

const Paths = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28 grid lg:grid-cols-12 gap-12">
    <div className="lg:col-span-4">
      <h2 className="font-serif-h text-4xl text-[#232b1b]">The medicine moves through:</h2>
      <div className="relative mt-10 hidden lg:block">
        <img src="/img/elders.jpg" alt="Indigenous elders in ceremonial dress" className="rounded-3xl aspect-[4/5] object-cover w-full" />
        <Ladybug spot="br" size={26} rotate={140} />
      </div>
    </div>
    <ol className="lg:col-span-8 divide-y divide-[#dacac1]">
      {PATHS.map(([to, name, desc], i) => (
        <li key={name}>
          <Link to={to} data-testid={`path-link-${i}`} className="group flex items-center gap-6 py-8 transition-colors duration-300">
            <span className="font-serif-h text-lg text-[#b6689a] w-10">0{i + 1}</span>
            <span className="flex-1">
              <span className="block font-serif-h text-3xl sm:text-4xl text-[#577731] group-hover:text-[#b6689a] transition-colors duration-300">{name}</span>
              <span className="block text-sm text-[#232b1b]/65 mt-1">{desc}</span>
            </span>
            <ArrowUpRight className="text-[#577731] group-hover:rotate-45 transition-transform duration-300" />
          </Link>
        </li>
      ))}
    </ol>
  </section>
);

const CEREMONY_CARDS = [
  ["light", "/img/lights.jpg", "The Light Ceremony", "November 22nd – 27th"],
  ["waterfall", "/img/waterfall.jpg", "Waterfall Medicine: Field Trip", "October 30th, 2026 (Tentative)"],
];

const Ceremonies = () => (
  <section className="bg-[#232b1b] text-[#faf7f2]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
      <span className="text-[11px] uppercase tracking-[0.3em] text-[#b1cc90]">Sacred Ceremony</span>
      <h2 className="font-serif-h text-4xl sm:text-5xl mt-4 max-w-2xl">Where community gathers and remembers</h2>
      <div className="grid md:grid-cols-2 gap-8 mt-14">
        {CEREMONY_CARDS.map(([key, img, title, date]) => (
          <Link key={key} to={`/members/rsvp/${key}`} data-testid={`home-ceremony-${key}`} className="group relative rounded-3xl overflow-hidden aspect-[4/5] block">
            <img src={img} alt={title} className="absolute inset-0 h-full w-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
            {key === "light" && <Ladybug spot="tl" size={24} rotate={60} />}
            <div className="absolute inset-x-0 bottom-0 p-8 bg-gradient-to-t from-black/85 to-transparent">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#b1cc90]"><Lock size={12} /> Ambassadors only</span>
              <h3 className="font-serif-h text-3xl mt-3">{title}</h3>
              <p className="text-sm text-white/75 mt-1">{date}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Law />
      <Paths />
      <Ceremonies />
      <Candles />
    </div>
  );
}
